import { useState } from "react";
import { Button, Form, Input, notification } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { AuthCard } from "../components/AuthCard/AuthCard";
import AuthFrame from "../components/PublicChrome/AuthFrame";
import AuthorizationService from "../../sdk/services/AuthService";

interface RegisterForm {
  fullName: string;
  userName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export default function RegistroView() {
  const navigate = useNavigate();
  const [form] = Form.useForm<RegisterForm>();
  const [loading, setLoading] = useState(false);

  const onSubmit = async (values: RegisterForm) => {
    setLoading(true);
    try {
      await AuthorizationService.register({
        email: values.email,
        userName: values.userName,
        fullName: values.fullName,
        password: values.password,
        profile: "free",
      });
      navigate("/verificar-email");
    } catch (error: any) {
      const msg = error?.response?.data?.error || "Erro ao criar conta. Tente novamente.";
      notification.error({ message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthFrame screen="registro">
      <AuthCard
        title="Crie sua conta"
        subtitle="Preencha os dados abaixo para começar"
      >
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        requiredMark={false}
      >
        <Form.Item
          name="fullName"
          rules={[{ required: true, message: "Informe seu nome completo" }]}
          className="mb-4"
        >
          <Input placeholder="Nome completo" size="large" />
        </Form.Item>

        <Form.Item
          name="userName"
          validateTrigger={["onBlur", "onSubmit"]}
          // Regras em ordem, parando na primeira falha: nome com espaço não
          // deve nem chegar à consulta de disponibilidade.
          validateFirst
          rules={[
            { required: true, message: "Informe um nome de usuário" },
            {
              pattern: /^\S+$/,
              message: "O nome de usuário não pode conter espaços — use algo como glauber.cruz ou glaubercruz",
            },
            {
              async validator(_, value) {
                if (!value) return;
                const res = await AuthorizationService.checkAvailability({ userName: value });
                if (res.userNameAvailable === false)
                  return Promise.reject(new Error("Nome de usuário já está em uso"));
              },
            },
          ]}
          className="mb-4"
        >
          <Input
            placeholder="Nome de usuário"
            size="large"
            autoCapitalize="none"
            autoCorrect="off"
          />
        </Form.Item>

        <Form.Item
          name="email"
          validateTrigger={["onBlur", "onSubmit"]}
          rules={[
            { required: true, message: "Informe seu e-mail" },
            { type: "email", message: "E-mail inválido" },
            {
              async validator(_, value) {
                if (!value) return;
                const res = await AuthorizationService.checkAvailability({ email: value });
                if (res.emailAvailable === false)
                  return Promise.reject(new Error("E-mail já cadastrado"));
              },
            },
          ]}
          className="mb-4"
        >
          <Input
            placeholder="E-mail"
            size="large"
            autoCapitalize="none"
            autoCorrect="off"
          />
        </Form.Item>

        <Form.Item
          name="password"
          rules={[
            { required: true, message: "Informe uma senha" },
            { min: 8, message: "A senha deve ter no mínimo 8 caracteres" },
          ]}
          className="mb-4"
        >
          <Input.Password placeholder="Senha" size="large" />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Confirme sua senha" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error("As senhas não coincidem"));
              },
            }),
          ]}
          className="mb-6"
        >
          <Input.Password placeholder="Confirmar senha" size="large" />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          block
          size="large"
          loading={loading}
          className="!bg-red-600 !border-red-600 hover:!bg-red-700 hover:!border-red-700 font-semibold"
        >
          Criar conta
        </Button>

        <p className="mt-5 text-center text-sm text-slate-500 dark:text-slate-400">
          Já tem conta?{" "}
          <Link
            to="/login"
            className="font-semibold text-red-600 hover:text-red-700 transition-colors"
          >
            Entrar
          </Link>
        </p>
      </Form>
      </AuthCard>
    </AuthFrame>
  );
}
